/**
 * Enhanced EmptyState Component
 * 
 * Comprehensive empty state system with multiple variants matching Figma designs.
 * Supports default, compact, and inline variants with proper spacing and accessibility.
 * 
 * Updated from basic EmptyState to match EmptyStatesPage.tsx specifications
 */

import type { ReactNode } from 'react';
import { cn } from '../lib/utils';

interface EmptyStateProps {
  variant?: 'default' | 'compact' | 'inline';
  icon: ReactNode;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Optional secondary action, rendered as a low-weight text button below the
   * primary (weight budget: max 1 primary + 1 secondary). Not shown in the
   * `inline` variant. Added for role/state-aware events empty states (#3059).
   */
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  className?: string;
}

const EmptyState = ({
  variant = 'default',
  icon,
  title,
  message,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  className = ''
}: EmptyStateProps) => {
  // Variant-specific styling
  // Note: compact variant has no shadow to match chat bubble design
  const containerClasses = {
    default: 'bg-oro-surface border border-oro-border rounded-xl p-8 shadow-card text-center',
    compact: 'bg-oro-surface border border-oro-border rounded-xl p-6 text-center',
    inline: 'bg-oro-surface-alt border border-oro-border rounded-xl p-4 shadow-card'
  };

  const iconClasses = {
    default: 'w-12 h-12', // 48px
    compact: 'w-8 h-8',   // 32px  
    inline: 'w-6 h-6'     // 24px
  };

  const layoutClasses = {
    default: 'space-y-4',
    compact: 'space-y-3',
    inline: 'flex items-center gap-3'
  };

  const textClasses = {
    default: {
      title: 'text-text-primary font-medium mb-3',
      message: 'text-text-secondary mb-6 max-w-sm mx-auto'
    },
    compact: {
      title: 'text-text-primary font-medium mb-2',
      message: 'text-text-muted'
    },
    inline: {
      title: 'sr-only', // Hidden for inline variant
      message: 'text-text-muted'
    }
  };

  if (variant === 'inline') {
    return (
      <div className={cn(containerClasses[variant], className)}>
        <div className={layoutClasses[variant]}>
          <div className={cn(iconClasses[variant], 'text-text-muted flex-shrink-0')}>
            {icon}
          </div>
          <span className={textClasses[variant].title}>{title}</span>
          <p className={textClasses[variant].message}>
            {message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(containerClasses[variant], className)}>
      <div className={layoutClasses[variant]}>
        {/* Icon */}
        <div className="flex justify-center">
          <div className={cn(iconClasses[variant], 'text-text-muted')}>
            {icon}
          </div>
        </div>
        
        {/* Title */}
        <h3 className={textClasses[variant].title}>
          {title}
        </h3>
        
        {/* Message */}
        <p className={textClasses[variant].message} style={{ lineHeight: '1.5' }}>
          {message}
        </p>
        
        {/* Action Button (primary) */}
        {actionLabel && onAction && (
          <button
            onClick={onAction}
            className="bg-accent hover:bg-accent-light active:bg-accent-dark transition-colors px-6 py-3 rounded-lg shadow-glow"
          >
            <span className="text-black font-medium">{actionLabel}</span>
          </button>
        )}

        {/* Secondary action — low-weight text button (#3059) */}
        {secondaryActionLabel && onSecondaryAction && (
          <button
            onClick={onSecondaryAction}
            className="text-text-secondary hover:text-accent font-medium text-sm transition-colors"
          >
            {secondaryActionLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default EmptyState;